import { CommonToolTip, NoRecordFound } from "components/Common/Common";
import { Button, Col, Row } from "react-bootstrap";
import formIcn from "Assets/images/formIcn.svg";
import { useLazyGetFileUrlQuery } from "store/services/utility";
import AddDocumentPop from "components/Modals/AddDocumentPop";
import { useEffect, useState } from "react";
import ConfirmationPop from "components/Modals/ConfirmationPop";
import { useDeleteResourceMutation, useRestoreResourceMutation } from "store/services/resource";
import { convertToMomentDate, dateFormat, successMessageToast } from "helpers/common";
import { cloneDeep } from "lodash";
import parse from "html-react-parser";
import { generatePath, useNavigate } from "react-router-dom";
import { VIEW_REPORT } from "config/paths";
import { DeleteIcon, RefreshIcon, ViewDocumentIcon } from "Assets/icons";
import UpArrowIcon from "Assets/images/UpArrowIcon";
import DownArrowIcon from "Assets/images/DownArrowIcon";
import NoRecordAddDetails from "components/NoRecordAddDetails";
import LoadingOverlay from "components/shared/LoadingOverlay";
import AddPlus from "Assets/images/AddPlus";

const MdtAttachments = ({ resources, uploadDocuments, hideAttachments = false }) => {

    const navigate = useNavigate();

    const [showDocumentModal, setShowDocumentModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showRestoreModal, setShowRestoreModal] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);
    const [resourcesList, setResourcesList] = useState([]);

    // Sort states for attachments
    const [attachmentSortField, setAttachmentSortField] = useState("date");
    const [attachmentSortOrder, setAttachmentSortOrder] = useState("desc");

    // Sort states for reports
    const [reportSortField, setReportSortField] = useState("date");
    const [reportSortOrder, setReportSortOrder] = useState("desc");

    const reports = resourcesList?.filter(x => x.type == "report");
    const attachments = resourcesList?.filter(x => x.type == "attachment");

    const [getFileUrl, { isLoading: getFileLoading, isFetching: getFileFetching }] = useLazyGetFileUrlQuery();
    const [removeFileApi, { isLoading: isRemoving }] = useDeleteResourceMutation();
    const [restoreFile, { isLoading: restoreFileLoading }] = useRestoreResourceMutation();

    const handleViewPdf = async (name) => {

        try {
            const result = await getFileUrl(name);
            if (result?.error) return;

            const url = result?.data
            if (url) window.open(url, '_blank');

        } catch (err) { console.error("Error assigning therapist", err); }
    };

    const handleDelete = async () => {

        try {
            setShowDeleteModal(false);

            const list = cloneDeep(resourcesList);
            const index = list.findIndex(x => x.id == currentItem?.id);

            if (index > -1) {

                list[index] = { ...list[index], deletedAt: new Date() };
                setResourcesList(list);
                const result = await removeFileApi(currentItem?.id);
                if (result?.error) return;

                successMessageToast(result?.data?.message);
            };
        } catch (err) { console.error("Error deleting document", err); }
    };

    const handleRestore = async () => {

        try {
            setShowRestoreModal(false);

            const list = cloneDeep(resourcesList);
            const index = list.findIndex(x => x.id == currentItem?.id);

            if (index > -1) {

                list[index] = { ...list[index], deletedAt: null };
                setResourcesList(list);
                const result = await restoreFile(currentItem?.id);
                if (result?.error) return;

                successMessageToast(result?.data?.message);
            };
        } catch (err) { console.error("Error restoring document", err); }
    };

    // Handle sort for attachments
    const handleAttachmentSort = (field) => {
        if (attachmentSortField === field) {
            setAttachmentSortOrder(attachmentSortOrder === "asc" ? "desc" : "asc");
        } else {
            setAttachmentSortField(field);
            setAttachmentSortOrder("desc");
        }
    };

    // Handle sort for reports
    const handleReportSort = (field) => {
        if (reportSortField === field) {
            setReportSortOrder(reportSortOrder === "asc" ? "desc" : "asc");
        } else {
            setReportSortField(field);
            setReportSortOrder("desc");
        }
    };

    // Sort attachments
    const sortedAttachments = attachments
        ? [...attachments].sort((a, b) => {
            let A = "", B = "";

            if (attachmentSortField === "date") {
                A = new Date(a?.createdAt || a?.updatedAt);
                B = new Date(b?.createdAt || b?.updatedAt);
            }

            if (attachmentSortField === "name") {
                A = a?.attachment_name?.toLowerCase() || "";
                B = b?.attachment_name?.toLowerCase() || "";
            }

            if (attachmentSortOrder === "asc") {
                return A > B ? 1 : -1;
            } else {
                return A < B ? 1 : -1;
            }
        })
        : [];

    // Sort reports
    const sortedReports = reports
        ? [...reports].sort((a, b) => {
            let A = "", B = "";

            if (reportSortField === "date") {
                A = new Date(a?.createdAt || a?.updatedAt);
                B = new Date(b?.createdAt || b?.updatedAt);
            }

            if (reportSortField === "name") {
                A = a?.attachment_name?.toLowerCase() || "";
                B = b?.attachment_name?.toLowerCase() || "";
            }

            if (reportSortField === "discipline") {
                A = a?.therapistResources?.therapist_detail?.therapist_discipline?.assign_term?.toLowerCase() || "";
                B = b?.therapistResources?.therapist_detail?.therapist_discipline?.assign_term?.toLowerCase() || "";
            }

            if (reportSortOrder === "asc") {
                return A > B ? 1 : -1;
            } else {
                return A < B ? 1 : -1;
            }
        })
        : [];

    // Get bg color according to type
    const bgColors = {
        consent: "#ECFFDB",
        document: "#DBF8FB",
        report: "#FFE9FA",
    };

    const getBgColor = (name) => bgColors[name?.toLowerCase()] || "transparent";

    useEffect(() => {

        if (resources?.length) setResourcesList(resources);
    }, [resources]);

    return (
        <div>
            <ConfirmationPop
                confirmation={showDeleteModal}
                setConfirmation={setShowDeleteModal}
                confirmHandler={handleDelete}
                type={"delete"}
            />

            <ConfirmationPop
                confirmation={showRestoreModal}
                setConfirmation={setShowRestoreModal}
                confirmHandler={handleRestore}
                type={"restore"}
            />

            <AddDocumentPop
                show={showDocumentModal}
                setShow={setShowDocumentModal}
                handleSubmit={(values) => {
                    setShowDocumentModal(false)
                    uploadDocuments(values);
                }}
            />
            {(getFileLoading || getFileFetching || isRemoving || restoreFileLoading) && <LoadingOverlay />}

            <Row>
                <Col lg="12" className="my-2">
                    {
                        !hideAttachments &&
                        <div style={{ border: "1px solid #C7C7C7", padding: "12px", borderRadius: "4px" }}>

                            <Row className="align-items-center">
                                <Col lg='8'>
                                    <div className="d-flex gap-2">
                                        <h6 className="mb-0 forteen proxima-regular">ATTACHMENTS
                                            {/* <span onClick={() => navigate(generatePath(CLINICIANS_HISTORY, { clientId, clientType }))} className="themeBlue cursor-pointer ten ms-2" style={{ textDecoration: "underline" }}>HISTORY</span> */}
                                        </h6>
                                    </div>
                                </Col>
                                <Col lg='4' className="text-end">
                                    {/* <Button
                                    style={{ borderRadius: "0" }}
                                    className="commonBtn borderedBtn bg-transparent"
                                    onClick={() => setShowDocumentModal(true)}
                                >
                                    Add Attachment
                                </Button> */}
                                    <AddPlus
                                        onClick={() => setShowDocumentModal(true)}
                                    />

                                </Col>
                            </Row>

                            {/* <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="m-0 themeBlue fw-bold">Attachments:</h5>
                            <Button
                                className="d-flex align-items-center justify-content-center commonBtn borderedBtn bg-transparent"
                                onClick={() => setShowDocumentModal(true)}
                            >
                                Add Attachment
                            </Button>
                        </div> */}

                            <div className="mt-2">
                                <table className="w-100">
                                    <thead>
                                        <tr>
                                            <th
                                                onClick={() => handleAttachmentSort("name")}
                                                className="fw-medium proxima"
                                                style={{ padding: "15px 10px", borderBottom: "2px solid #315D99", cursor: "pointer" }}
                                            >
                                                <div className={`${attachmentSortField === 'name' && "themeBlueV2"} d-flex gap-2 align-items-center`}>
                                                    NAME
                                                    {attachmentSortField === "name" ?
                                                        (attachmentSortOrder === "asc" ? <UpArrowIcon color={attachmentSortOrder === "asc"} /> : <DownArrowIcon color={attachmentSortOrder === "desc"} />) :
                                                        <DownArrowIcon />
                                                    }
                                                </div>
                                            </th>
                                            <th
                                                onClick={() => handleAttachmentSort("date")}
                                                className="fw-medium proxima"
                                                style={{ padding: "15px 10px", borderBottom: "2px solid #315D99", cursor: "pointer" }}
                                            >
                                                <div className={`${attachmentSortField === 'date' && "themeBlueV2"} d-flex gap-2 align-items-center`}>
                                                    DATE
                                                    {attachmentSortField === "date" ?
                                                        (attachmentSortOrder === "asc" ? <UpArrowIcon color={attachmentSortOrder === "asc"} /> : <DownArrowIcon color={attachmentSortOrder === "desc"} />) :
                                                        <DownArrowIcon />
                                                    }
                                                </div>
                                            </th>
                                            <th
                                                className="fw-medium proxima"
                                                style={{ padding: "15px 10px", borderBottom: "2px solid #315D99" }}
                                            >
                                                <div className="d-flex gap-2 align-items-center">
                                                    ACTION
                                                </div>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedAttachments?.length > 0 ?
                                            sortedAttachments?.map((item, index) => {
                                                return (
                                                    <tr key={index}>
                                                        <td
                                                            style={{
                                                                padding: "15px 10px",
                                                                borderRadius: 0,
                                                                borderBottom: "1px solid #8F8F8F",
                                                            }}
                                                        >
                                                            <div className="d-flex align-items-center gap-10">
                                                                <span className="icn flex-shrink-0">
                                                                    <img
                                                                        src={formIcn}
                                                                        alt=""
                                                                        className="img-fluid"
                                                                        style={{ width: "20px" }}
                                                                    />
                                                                </span>
                                                                <CommonToolTip
                                                                    text={item?.attachment_name}
                                                                    component={
                                                                        <p
                                                                            style={{
                                                                                maxWidth: "250px",
                                                                                textOverflow: "ellipsis",
                                                                                overflow: "hidden",
                                                                                fontSize: "12px",
                                                                                color: "#000",
                                                                                margin: 0,
                                                                                cursor: "pointer"
                                                                            }}
                                                                            className="proxima"
                                                                            role="button"
                                                                            onClick={() => handleViewPdf(item?.attachment_key)}
                                                                        >
                                                                            {item?.attachment_name || "--"}
                                                                        </p>
                                                                    }
                                                                />
                                                            </div>
                                                        </td>
                                                        <td
                                                            className="proxima"
                                                            style={{
                                                                padding: "15px 10px",
                                                                borderRadius: 0,
                                                                borderBottom: "1px solid #8F8F8F",
                                                                color: "#000",
                                                                fontSize: "12px",
                                                            }}
                                                        >
                                                            {dateFormat(item?.createdAt, "MMM DD, YYYY")}
                                                        </td>
                                                        <td
                                                            style={{
                                                                padding: "15px 10px",
                                                                borderRadius: 0,
                                                                borderBottom: "1px solid #8F8F8F",
                                                            }}
                                                        >
                                                            <div className="d-flex align-items-center gap-10">
                                                                <div style={{ backgroundColor: "#ebffeb", padding: "6px", borderRadius: "100px" }}>
                                                                    <CommonToolTip
                                                                        text={"View"}
                                                                        component={
                                                                            <ViewDocumentIcon
                                                                                style={{ width: "15px", height: "15px", cursor: "pointer" }}
                                                                                onClick={() => handleViewPdf(item?.attachment_key)}
                                                                            />
                                                                        }
                                                                    />
                                                                </div>
                                                                <div style={{ backgroundColor: "#ebffeb", padding: "6px", borderRadius: "100px" }}>
                                                                    {!item?.deletedAt ?
                                                                        <CommonToolTip
                                                                            text={"Delete"}
                                                                            component={
                                                                                <DeleteIcon
                                                                                    className="cursor-pointer"
                                                                                    style={{ cursor: "pointer" }}
                                                                                    onClick={() => {
                                                                                        setCurrentItem(item);
                                                                                        setShowDeleteModal(true);
                                                                                    }}
                                                                                />
                                                                            }
                                                                        />
                                                                        :
                                                                        <CommonToolTip
                                                                            text={"Retrieve"}
                                                                            component={
                                                                                <RefreshIcon
                                                                                    className="cursor-pointer mx-1"
                                                                                    style={{ cursor: "pointer" }}
                                                                                    onClick={() => {
                                                                                        setCurrentItem(item);
                                                                                        setShowRestoreModal(true);
                                                                                    }}
                                                                                />
                                                                            }
                                                                        />
                                                                    }
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            }) :
                                            <tr>
                                                <td colSpan="3" className="text-center px-0">
                                                    <NoRecordAddDetails onClick={() => setShowDocumentModal(true)} title='Add a document' />
                                                </td>
                                            </tr>
                                        }
                                    </tbody>
                                </table>
                            </div>

                        </div>
                    }

                    <div className="mt-3" style={{ border: "1px solid #C7C7C7", padding: "12px", borderRadius: "4px" }}>

                        <Row className="align-items-center">
                            <Col lg='8'>
                                <div className="d-flex gap-2">
                                    <h6 className="mb-0 forteen proxima-regular">REPORTS
                                        {/* <span onClick={() => navigate(generatePath(CLINICIANS_HISTORY, { clientId, clientType }))} className="themeBlue cursor-pointer ten ms-2" style={{ textDecoration: "underline" }}>HISTORY</span> */}
                                    </h6>
                                </div>
                            </Col>
                            <Col lg='4' className="text-end">
                                {/* <Button
                                style={{borderRadius: "0"}}
                                    className="commonBtn borderedBtn bg-transparent"
                                    onClick={() => setShowDocumentModal(true)}
                                >
                                    Add Attachment
                                </Button> */}
                            </Col>
                        </Row>
                        {/* <div className="my-4">
                            <h6 className="m-0 themeBlue fw-bold">Reports:</h6>
                        </div> */}

                        <div className="mt-2">
                            <table className="w-100">
                                <thead>
                                    <tr>
                                        <th
                                            onClick={() => handleReportSort("name")}
                                            className="fw-medium proxima"
                                            style={{ padding: "15px 10px", borderBottom: "2px solid #315D99", cursor: "pointer" }}
                                        >
                                            <div className={`${reportSortField === 'name' && "themeBlueV2"} d-flex gap-2 align-items-center`}>
                                                REPORT NAME
                                                {reportSortField === "name" ?
                                                    (reportSortOrder === "asc" ? <UpArrowIcon color={reportSortOrder === "asc"} /> : <DownArrowIcon color={reportSortOrder === "desc"} />) :
                                                    <DownArrowIcon />
                                                }
                                            </div>
                                        </th>
                                        <th
                                            onClick={() => handleReportSort("discipline")}
                                            className="fw-medium proxima"
                                            style={{ padding: "15px 10px", borderBottom: "2px solid #315D99", cursor: "pointer" }}
                                        >
                                            <div className={`${reportSortField === 'discipline' && "themeBlueV2"} d-flex gap-2 align-items-center`}>
                                                DISCIPLINE
                                                {reportSortField === "discipline" ?
                                                    (reportSortOrder === "asc" ? <UpArrowIcon color={reportSortOrder === "asc"} /> : <DownArrowIcon color={reportSortOrder === "desc"} />) :
                                                    <DownArrowIcon />
                                                }
                                            </div>
                                        </th>
                                        <th
                                            onClick={() => handleReportSort("date")}
                                            className="fw-medium proxima"
                                            style={{ padding: "15px 10px", borderBottom: "2px solid #315D99", cursor: "pointer" }}
                                        >
                                            <div className={`${reportSortField === 'date' && "themeBlueV2"} d-flex gap-2 align-items-center`}>
                                                DATE
                                                {reportSortField === "date" ?
                                                    (reportSortOrder === "asc" ? <UpArrowIcon color={reportSortOrder === "asc"} /> : <DownArrowIcon color={reportSortOrder === "desc"} />) :
                                                    <DownArrowIcon />
                                                }
                                            </div>
                                        </th>
                                        <th
                                            className="fw-medium proxima"
                                            style={{ padding: "15px 10px", borderBottom: "2px solid #315D99" }}
                                        >
                                            <div className="d-flex gap-2 align-items-center">
                                                ACTION
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedReports?.length > 0 ?
                                        sortedReports?.map((item, index) => {
                                            return (
                                                <tr key={index}>
                                                    <td
                                                        style={{
                                                            padding: "15px 10px",
                                                            borderRadius: 0,
                                                            borderBottom: "1px solid #8F8F8F",
                                                        }}
                                                    >
                                                        <div className="d-flex align-items-center gap-10">
                                                            <span className="icn flex-shrink-0">
                                                                <img
                                                                    src={formIcn}
                                                                    alt=""
                                                                    className="img-fluid"
                                                                    style={{ width: "20px" }}
                                                                />
                                                            </span>
                                                            <CommonToolTip
                                                                text={item?.attachment_name}
                                                                component={
                                                                    <p
                                                                        style={{
                                                                            maxWidth: "250px",
                                                                            textOverflow: "ellipsis",
                                                                            overflow: "hidden",
                                                                            fontSize: "12px",
                                                                            color: "#000",
                                                                            margin: 0,
                                                                            cursor: "pointer"
                                                                        }}
                                                                        className="proxima"
                                                                        role="button"
                                                                        onClick={() => handleViewPdf(item?.attachment_key)}
                                                                    >
                                                                        {parse(item?.attachment_name) || "--"}
                                                                    </p>
                                                                }
                                                            />
                                                        </div>
                                                    </td>
                                                    <td
                                                        className="proxima"
                                                        style={{
                                                            padding: "15px 10px",
                                                            borderRadius: 0,
                                                            borderBottom: "1px solid #8F8F8F",
                                                            color: "#000",
                                                            fontSize: "12px",
                                                        }}
                                                    >
                                                        <span
                                                            key={index}
                                                            className={`discipline-${item?.therapistResources?.therapist_detail?.therapist_discipline?.assign_term}-capsule-bcg`}
                                                            style={{ borderRadius: "8px", padding: "7px 10px", }}
                                                        >
                                                            {item?.therapistResources?.therapist_detail?.therapist_discipline?.assign_term || "--"}
                                                        </span>
                                                    </td>
                                                    <td
                                                        className="proxima"
                                                        style={{
                                                            padding: "15px 10px",
                                                            borderRadius: 0,
                                                            borderBottom: "1px solid #8F8F8F",
                                                            color: "#000",
                                                            fontSize: "12px",
                                                        }}
                                                    >
                                                        {dateFormat(item?.createdAt, "MMM DD, YYYY")}
                                                    </td>
                                                    <td
                                                        style={{
                                                            padding: "15px 10px",
                                                            borderRadius: 0,
                                                            borderBottom: "1px solid #8F8F8F",
                                                        }}
                                                    >
                                                        <div className="d-flex align-items-center gap-10">
                                                            <div style={{ backgroundColor: "#ebffeb", padding: "6px", borderRadius: "100px" }}>
                                                                <CommonToolTip
                                                                    text={"View"}
                                                                    component={
                                                                        <ViewDocumentIcon
                                                                            style={{ width: "15px", height: "15px", cursor: "pointer" }}
                                                                            onClick={() => navigate(generatePath(VIEW_REPORT, { attachmentKey: item.attachment_key }))}
                                                                        />
                                                                    }
                                                                />
                                                            </div>
                                                            <div style={{ backgroundColor: "#ebffeb", padding: "6px", borderRadius: "100px" }}>
                                                                {!item?.deletedAt ?
                                                                    <CommonToolTip
                                                                        text={"Delete"}
                                                                        component={
                                                                            <DeleteIcon
                                                                                className="cursor-pointer"
                                                                                style={{ cursor: "pointer" }}
                                                                                onClick={() => {
                                                                                    setCurrentItem(item);
                                                                                    setShowDeleteModal(true);
                                                                                }}
                                                                            />
                                                                        }
                                                                    />
                                                                    :
                                                                    <CommonToolTip
                                                                        text={"Retrieve"}
                                                                        component={
                                                                            <RefreshIcon
                                                                                className="cursor-pointer mx-1"
                                                                                style={{ cursor: "pointer" }}
                                                                                onClick={() => {
                                                                                    setCurrentItem(item);
                                                                                    setShowRestoreModal(true);
                                                                                }}
                                                                            />
                                                                        }
                                                                    />
                                                                }
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        }) :
                                        <tr>
                                            <td colSpan="4" className="text-center px-0">
                                                <div style={{ cursor: "pointer", textAlign: "center", borderRadius: "12px" }}>
                                                    <div style={{ backgroundColor: "#eaeff5", padding: "24px", borderRadius: "12px" }} className='d-flex gap-2 align-items-center justify-content-center'>
                                                        <span style={{ color: "#315D99" }}>{"No Record Found"}</span>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    }
                                </tbody>
                            </table>
                        </div>
                    </div>
                </Col>
            </Row>
        </div>
    )
};

export default MdtAttachments;